import React, { useEffect, useState } from 'react';
import { Layout, Menu, Button, Typography, Avatar, Space, Badge } from 'antd';
import {
    CloudUploadOutlined,
    FileSearchOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    UserOutlined,
    UnorderedListOutlined,
    LogoutOutlined,
    AuditOutlined,
    TeamOutlined,
    ControlOutlined,
    CalculatorOutlined,
    MedicineBoxOutlined
} from '@ant-design/icons';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { Navigate, useNavigate } from 'react-router-dom';

import UploadPage from '../pages/UploadPage';
import logo from '../assets/audidox-logo.jpeg';
import { useWebSocket } from '../WebSocketContext';
import ApiClient from '../api';
import Mycases from './Mycases';
import AllTasks from '../components/AllTasks';
import RulesEditor from '../pages/RulesEditor';
import ManageUsers from '../pages/ManageUsers';
import DoctorsList from '../pages/DoctorsList';
import { user_pool_id, client_id } from '../utils/url_constants';
import AdminPanel from "./AdminPanel";
import SocEditor from "./SocEditor";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const api_client = new ApiClient();

const Dashboard = () => {
    const navigate = useNavigate();
    const { ws, messages: wsMessages } = useWebSocket();
    const [loggedOut, setLoggedOut] = useState(false);

    const [collapsed, setCollapsed] = useState(false);
    const [selectedKey, setSelectedKey] = useState('upload');
    const [casesCount, setCasesCount] = useState(0);
    const [userInfo, setUserInfo] = useState({ name: '', surname: '', roleType: '' });

    const handleLogout = async () => {
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.close(1000, 'User logout');
        }
        await new Promise(resolve => setTimeout(resolve, 100));

        const poolData = { UserPoolId: user_pool_id, ClientId: client_id };
        const userPool = new CognitoUserPool(poolData);
        const cognitoUser = userPool.getCurrentUser();
        if (cognitoUser) cognitoUser.signOut();

        localStorage.removeItem('email');
        localStorage.removeItem('user_access');
        localStorage.removeItem('lastUsername');
        setLoggedOut(true);
    };

    const renderContent = () => {
        switch (selectedKey) {
            case 'upload':       return <UploadPage />;
            case 'mycases':      return <Mycases wsMessages={wsMessages} />;
            case 'tasks':        return <AllTasks wsMessages={wsMessages} />;
            case 'rules':        return <RulesEditor />;
            case 'soc':          return <SocEditor />;
            case 'doctors':      return <DoctorsList />;
            case 'manageusers':  return <ManageUsers />;
            case 'admin_panel':  return <AdminPanel />;
            default:             return <UploadPage />;
        }
    };

    useEffect(() => {
        const initializeDashboard = async () => {
            const email = localStorage.getItem('email');
            const accessData = localStorage.getItem('user_access');

            if (accessData) {
                try {
                    const parsed = JSON.parse(accessData);
                    setUserInfo({
                        name:     parsed.name     || '',
                        surname:  parsed.surname  || '',
                        roleType: parsed.roletype || '',
                    });
                } catch (e) {
                    console.error('Failed to parse user_access JSON', e);
                }
            }

            if (email) {
                const unread = await api_client.get_unread_count(email);
                setCasesCount(unread?.unread_counter || 0);
            }
        };

        initializeDashboard();
    }, []);

    useEffect(() => {
        if (!wsMessages || wsMessages.length === 0) return;
        const newMessage = wsMessages[wsMessages.length - 1];
        if (newMessage?.new_unread_count !== undefined && newMessage?.new_unread_count !== null) {
            setCasesCount(newMessage.new_unread_count);
        }
    }, [wsMessages]);

    const menuItems = [
        {
            key: 'upload',
            icon: <CloudUploadOutlined />,
            label: 'Upload Files',
        },
        {
            key: 'mycases',
            icon: <FileSearchOutlined />,
            label: (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    My Cases
                    {casesCount > 0 && !collapsed && (
                        <Badge
                            count={casesCount}
                            size="large"
                            style={{ backgroundColor: '#79A7A5', color: '#0D0F14', fontWeight: 'bold' }}
                        />
                    )}
                </span>
            ),
        },
        {
            key: 'tasks',
            icon: <UnorderedListOutlined />,
            label: 'All Tasks',
        },
        ...(userInfo.roleType === 'AUDIDOX' ? [
            {
                key: 'rules',
                icon: <AuditOutlined />,
                label: 'Rules Editor',
            },
            {
                key: 'soc',
                icon: <CalculatorOutlined />,
                label: 'Scale of Costs Editor',
            },
            {
                key: 'doctors',
                icon: <MedicineBoxOutlined />,
                label: 'Manage Doctors',
            },
            {
                key: 'admin_panel',
                icon: <ControlOutlined />,
                label: 'Admin Panel',
            },

        ] : []),
        {
            key: 'manageusers',
            icon: <TeamOutlined />,
            label: 'Manage Users',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined style={{ color: '#ff4d4f' }} />,
            label: <span style={{ color: '#ff4d4f' }}>Logout</span>,
        },
    ];

    if (loggedOut) return <Navigate to="/" />;

    return (
        <Layout style={{ minHeight: '100vh', background: 'var(--bg-base)' }}>
            <Sider
                trigger={null}
                collapsible
                collapsed={collapsed}
                width={260}
                style={{
                    background: 'var(--bg-surface)',
                    borderRight: '1px solid var(--border-default)',
                    position: 'fixed',
                    height: '100vh',
                    left: 0,
                    zIndex: 10,
                }}
            >
                <div style={{ height: 64, padding: '16px', borderBottom: '1px solid var(--border-default)' }}>
                    {!collapsed && (
                        <img
                            src={logo}
                            alt="AudiDox Logo"
                            style={{ width: '175px', display: 'block', margin: '0 auto' }}
                        />
                    )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', justifyContent: 'space-between' }}>
                    <Menu
                        mode="inline"
                        selectedKeys={[selectedKey]}
                        onClick={e => e.key === 'logout' ? handleLogout() : setSelectedKey(e.key)}
                        style={{ background: 'transparent', borderRight: 0, marginTop: 16 }}
                        items={menuItems}
                    />
                </div>
            </Sider>

            <Layout style={{ marginLeft: collapsed ? 80 : 260, transition: 'all 0.2s' }}>
                <Header style={{
                    background: 'var(--bg-surface)',
                    borderBottom: '1px solid var(--border-default)',
                    height: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 24px',
                }}>
                    <Button
                        type="text"
                        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        onClick={() => setCollapsed(!collapsed)}
                        style={{ color: 'var(--icon-primary)' }}
                    />
                    <Space>
                        <Avatar icon={<UserOutlined />} style={{ backgroundColor: 'var(--bg-accent)' }} />
                        <Text style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                            {userInfo.name} {userInfo.surname}
                        </Text>
                    </Space>
                </Header>

                <Content style={{
                    background: 'var(--bg-base)',
                    borderRadius: 12,
                }}>
                    {renderContent()}
                </Content>
            </Layout>
        </Layout>
    );
};

export default Dashboard;